/**
 * JobNotesTab
 *
 * Features:
 *  - Rich text editor (bold, italic, underline, bullets, numbered list, code)
 *  - Work item selection is MANDATORY — "Add Note" is blocked until one is chosen
 *  - Once a work item is picked its MEASUREMENT PANEL is revealed above the editor —
 *    sliders, IN/OUT toggle, add/remove, completeness badges. This is the same
 *    panel that previously lived inside each WorkItemCard on the Work Items tab.
 *  - Notes are grouped by work item after saving, each group collapsible
 *  - Attach images from gallery or camera (capture="environment")
 *  - Progress dialog: blocks UI during save → upload sequence with per-step feedback
 *  - After save+upload completes images appear immediately; onRefreshJob() syncs the store
 *  - Images rendered via /api/job-note-images/{uuid}/url (presigned S3 redirect)
 *
 * Props:
 *   job             – full job object (job.uuid, job.notes[], job.workItems[])
 *   onNoteAdded     – async ({ content, noteType, customerVisible, workItemUuid }) → updatedJob
 *   onRefreshJob    – async () → updatedJob  (called after images upload to sync store)
 *   operationLoading – bool
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    MessageSquare, Plus, Bold, Italic, Underline, List, ListOrdered,
    Code, Camera, Image as ImageIcon, X, ChevronLeft, ChevronRight,
    Loader2, Eye, Lock, ZoomIn, AlertCircle, CheckCircle2,
    Wrench, ChevronDown, ChevronUp,
} from 'lucide-react';
import { uploadNoteImageApi } from '../services/jobNoteImageApi';
import axiosInstance from '../services/axiosInstance';
import { safeNoteHtml } from '../utils/safeHtml';

// ── Constants ─────────────────────────────────────────────────────────────────

const NOTE_TYPE_OPTIONS = [
    { value: 'GENERAL',                label: 'General' },
    { value: 'INSPECTION',             label: 'Inspection' },
    { value: 'PROGRESS_UPDATE',        label: 'Progress Update' },
    { value: 'CUSTOMER_COMMUNICATION', label: 'Customer Comms' },
    { value: 'PARTS_ORDER',            label: 'Parts Order' },
    { value: 'HANDOVER',               label: 'Handover' },
];

const NOTE_TYPE_COLORS = {
    GENERAL:                'bg-gray-100 text-gray-600',
    INSPECTION:             'bg-blue-100 text-blue-700',
    PROGRESS_UPDATE:        'bg-violet-100 text-violet-700',
    CUSTOMER_COMMUNICATION: 'bg-emerald-100 text-emerald-700',
    PARTS_ORDER:            'bg-amber-100 text-amber-700',
    HANDOVER:               'bg-rose-100 text-rose-700',
};

const WORK_ITEM_STATUS_BADGE = {
    PENDING:               'bg-gray-100 text-gray-500',
    AWAITING_CONFIRMATION: 'bg-amber-100 text-amber-700',
    CONFIRMED:             'bg-blue-100 text-blue-700',
    IN_PROGRESS:           'bg-violet-100 text-violet-700',
    COMPLETED:             'bg-emerald-100 text-emerald-700',
    DECLINED:              'bg-red-100 text-red-500',
};

const initials = (name) =>
    (name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

const formatDate = (iso) =>
    new Date(iso).toLocaleString('en-AU', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });

const noteTypeLabel = (t) =>
    NOTE_TYPE_OPTIONS.find(o => o.value === t)?.label || t;

const truncate = (str, n = 55) =>
    str && str.length > n ? str.slice(0, n).trimEnd() + '…' : str;

/**
 * Decode the handful of HTML entities a contenteditable might emit so that
 * legacy notes saved before the outgoing-content normalisation don't render
 * `&nbsp;` (or other entities) literally in the plain-text render branch.
 * Order matters: decode &amp; LAST so we don't double-decode.
 */
const decodeBasicEntities = (s) =>
    typeof s !== 'string' ? s :
        s.replace(/&nbsp;/g, ' ')
            .replace(/&lt;/g,   '<')
            .replace(/&gt;/g,   '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g,  "'")
            .replace(/&amp;/g,  '&');

// ── Progress Dialog ───────────────────────────────────────────────────────────

function ProgressDialog({ steps, error }) {
    return (
        <div className="fixed inset-0 z-[300] bg-black/60 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4">Saving note…</h3>
                <div className="space-y-3">
                    {steps.map((step, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <div className="w-7 h-7 shrink-0 flex items-center justify-center">
                                {step.status === 'done'    && <CheckCircle2 size={20} className="text-emerald-500" />}
                                {step.status === 'active'  && <Loader2 size={20} className="text-violet-600 animate-spin" />}
                                {step.status === 'error'   && <AlertCircle size={20} className="text-red-500" />}
                                {step.status === 'pending' && <div className="w-4 h-4 rounded-full border-2 border-gray-200" />}
                            </div>
                            <span className={`text-sm ${
                                step.status === 'active'  ? 'text-violet-700 font-medium' :
                                    step.status === 'done'    ? 'text-gray-500 line-through' :
                                        step.status === 'error'   ? 'text-red-600 font-medium' :
                                            'text-gray-400'
                            }`}>
                                {step.label}
                            </span>
                        </div>
                    ))}
                </div>
                {error && (
                    <div className="mt-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                        <AlertCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
                        <p className="text-xs text-red-700 leading-snug">{error}</p>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Lightbox ──────────────────────────────────────────────────────────────────

function Lightbox({ images, startIndex, onClose }) {
    const [current, setCurrent]   = useState(startIndex);
    const [loadedUrls, setLoaded] = useState({});
    const [loading, setLoading]   = useState(false);

    const load = useCallback(async (img) => {
        if (img.localUrl) { setLoaded(prev => ({ ...prev, [img.uuid]: img.localUrl })); return; }
        if (loadedUrls[img.uuid] !== undefined) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/job-note-images/${img.uuid}/url`);
            setLoaded(prev => ({ ...prev, [img.uuid]: res.data.url }));
        } catch {
            setLoaded(prev => ({ ...prev, [img.uuid]: null }));
        } finally {
            setLoading(false);
        }
    }, [loadedUrls]);

    useEffect(() => { load(images[current]); }, [current]); // eslint-disable-line

    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'ArrowLeft')  setCurrent(c => Math.max(0, c - 1));
            if (e.key === 'ArrowRight') setCurrent(c => Math.min(images.length - 1, c + 1));
            if (e.key === 'Escape')     onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []); // eslint-disable-line

    const img = images[current];
    const url = loadedUrls[img.uuid];

    return (
        <div className="fixed inset-0 z-[200] bg-black/90 flex flex-col items-center justify-center" onClick={onClose}>
            <button className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white" onClick={onClose}>
                <X size={22} />
            </button>
            <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
                {current + 1} / {images.length}
            </div>
            <div className="max-w-[90vw] max-h-[80vh] flex items-center justify-center" onClick={e => e.stopPropagation()}>
                {loading || url === undefined ? (
                    <Loader2 size={36} className="text-white animate-spin" />
                ) : url === null ? (
                    <div className="text-white/60 text-sm">Failed to load image</div>
                ) : (
                    <img src={url} alt={img.originalFilename} className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl" />
                )}
            </div>
            {url && <p className="absolute bottom-6 text-white/50 text-xs">{img.originalFilename}</p>}
            {images.length > 1 && (
                <>
                    <button disabled={current === 0} onClick={e => { e.stopPropagation(); setCurrent(c => Math.max(0, c - 1)); }}
                            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/25 rounded-full text-white disabled:opacity-20">
                        <ChevronLeft size={26} />
                    </button>
                    <button disabled={current === images.length - 1} onClick={e => { e.stopPropagation(); setCurrent(c => Math.min(images.length - 1, c + 1)); }}
                            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/25 rounded-full text-white disabled:opacity-20">
                        <ChevronRight size={26} />
                    </button>
                </>
            )}
        </div>
    );
}

// ── Thumbnail strip ───────────────────────────────────────────────────────────

function NoteThumbnails({ images, onOpen }) {
    const [thumbUrls, setThumbUrls] = useState({});

    useEffect(() => {
        images.forEach(async (img) => {
            if (img.localUrl) { setThumbUrls(prev => ({ ...prev, [img.uuid]: img.localUrl })); return; }
            if (thumbUrls[img.uuid] !== undefined) return;
            try {
                const res = await axiosInstance.get(`/job-note-images/${img.uuid}/url`);
                setThumbUrls(prev => ({ ...prev, [img.uuid]: res.data.url }));
            } catch {
                setThumbUrls(prev => ({ ...prev, [img.uuid]: null }));
            }
        });
    }, [images]); // eslint-disable-line

    if (!images || images.length === 0) return null;

    return (
        <div className="flex flex-wrap gap-2 mt-2.5">
            {images.map((img, idx) => {
                const url = thumbUrls[img.uuid];
                return (
                    <button key={img.uuid} onClick={() => onOpen(idx)}
                            className="w-16 h-16 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-violet-400 transition-colors relative group shrink-0 bg-gray-100"
                            title={img.originalFilename}>
                        {url === undefined ? (
                            <Loader2 size={16} className="absolute inset-0 m-auto text-gray-400 animate-spin" />
                        ) : url === null ? (
                            <AlertCircle size={16} className="absolute inset-0 m-auto text-red-400" />
                        ) : (
                            <>
                                <img src={url} alt={img.originalFilename} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                                    <ZoomIn size={16} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            </>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

// ── Toolbar button ────────────────────────────────────────────────────────────

function ToolbarBtn({ onMouseDown, active, title, children }) {
    return (
        <button
            type="button"
            onMouseDown={onMouseDown}
            title={title}
            className={`w-7 h-7 flex items-center justify-center rounded text-sm transition-colors select-none
                ${active ? 'bg-violet-100 text-violet-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'}`}
        >
            {children}
        </button>
    );
}

// ── Add Note Form ─────────────────────────────────────────────────────────────

function AddNoteForm({ jobUuid, workItems = [], onNoteAdded, onRefreshJob, renderMeasurementsPanel, compact = false }) {
    const editorRef  = useRef(null);
    const galleryRef = useRef(null);
    const cameraRef  = useRef(null);

    const [noteType,        setNoteType]        = useState('PROGRESS_UPDATE');
    // Default to customer-visible so notes flow into completion reports unless
    // the tech explicitly marks them internal. Sticky across consecutive
    // note submissions — resets only when the tab is unmounted.
    const [customerVisible, setCustomerVisible] = useState(true);
    // Auto-select when there's exactly one work item
    const [workItemUuid,    setWorkItemUuid]     = useState(() =>
        workItems.length === 1 ? workItems[0].uuid : ''
    );
    const [pendingFiles,    setPendingFiles]     = useState([]);
    const [submitError,     setSubmitError]      = useState(null);
    const [isEmpty,         setIsEmpty]          = useState(true);
    const [activeFormats,   setActiveFormats]    = useState({});
    const [workItemError,   setWorkItemError]    = useState(false);

    const [progressSteps, setProgressSteps] = useState(null);
    const [progressError, setProgressError] = useState(null);

    const setStepStatus = (idx, status) =>
        setProgressSteps(prev => prev.map((s, i) => i === idx ? { ...s, status } : s));

    // Auto-select when there's exactly one work item (handles async load)
    useEffect(() => {
        if (workItems.length === 1 && !workItemUuid) {
            setWorkItemUuid(workItems[0].uuid);
        }
    }, [workItems]); // eslint-disable-line

    // ── Editor helpers ──────────────────────────────────────────────────────

    const execCmd = (cmd, value = null) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        document.execCommand(cmd, false, value);
        updateActiveFormats();
        handleEditorInput();
    };

    const updateActiveFormats = () => {
        setActiveFormats({
            bold:      document.queryCommandState('bold'),
            italic:    document.queryCommandState('italic'),
            underline: document.queryCommandState('underline'),
            ul:        document.queryCommandState('insertUnorderedList'),
            ol:        document.queryCommandState('insertOrderedList'),
        });
    };

    const handleEditorInput = () => {
        const el = editorRef.current;
        if (!el) return;
        const text = el.innerText.replace(/[\u200B\u00A0]/g, '').trim();
        setIsEmpty(text === '');
        updateActiveFormats();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.shiftKey) {
            e.preventDefault();
            document.execCommand('insertLineBreak', false, null);
        }
    };

    const handleSelectionChange = () => {
        if (document.activeElement === editorRef.current) updateActiveFormats();
    };

    useEffect(() => {
        document.addEventListener('selectionchange', handleSelectionChange);
        return () => document.removeEventListener('selectionchange', handleSelectionChange);
    }, []); // eslint-disable-line

    // ── File handling ───────────────────────────────────────────────────────

    const handleFiles = (files) => {
        // Accept files when EITHER the MIME type OR the filename extension matches.
        // Some platforms (notably iOS HEIC and certain Android browsers) deliver
        // images with empty or non-standard `file.type` — we don't want to drop
        // those silently.
        const allowedTypes = [
            'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
            'image/webp', 'image/heic', 'image/heif', 'image/bmp',
        ];
        const allowedExtensions = /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i;

        const next = [];
        const rejected = [];

        for (const file of files) {
            const typeOk = !!file.type && allowedTypes.includes(file.type.toLowerCase());
            const extOk  = allowedExtensions.test(file.name || '');

            if (!typeOk && !extOk) {
                rejected.push(`"${file.name || 'file'}" (${file.type || 'unknown type'})`);
                continue;
            }
            if (file.size > 10 * 1024 * 1024) {
                rejected.push(`"${file.name}" exceeds 10 MB`);
                continue;
            }
            next.push({ file, previewUrl: URL.createObjectURL(file), id: Math.random().toString(36).slice(2) });
        }

        // Surface rejections instead of silently dropping them.
        if (rejected.length) {
            setSubmitError(`Could not attach: ${rejected.join(', ')}`);
        }
        setPendingFiles(prev => [...prev, ...next].slice(0, 10));
    };

    const removeFile = (id) => {
        setPendingFiles(prev => {
            const f = prev.find(f => f.id === id);
            if (f) URL.revokeObjectURL(f.previewUrl);
            return prev.filter(f => f.id !== id);
        });
    };

    // ── Submit ──────────────────────────────────────────────────────────────

    const handleSubmit = async () => {
        const el = editorRef.current;
        // Strip zero-width spaces and normalise contenteditable's trailing `&nbsp;`
        // entities (and stray U+00A0 chars) to regular spaces, otherwise the saved
        // note shows `&nbsp;` literally in any consumer that renders it as text.
        const html      = (el?.innerHTML || '')
            .replace(/\u200B/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/\u00A0/g, ' ')
            .trim();
        const plainText = (el?.innerText  || '').replace(/[\u200B\u00A0]/g, '').trim();
        if (!plainText && pendingFiles.length === 0) return;

        // Work item is mandatory
        if (!workItemUuid) {
            setWorkItemError(true);
            return;
        }

        setSubmitError(null);
        setWorkItemError(false);
        setProgressError(null);

        const hasImages = pendingFiles.length > 0;
        const steps = [
            { label: 'Saving note', status: 'active' },
            ...(hasImages ? pendingFiles.map(f => ({ label: `Uploading ${f.file.name}`, status: 'pending' })) : []),
        ];
        setProgressSteps(steps);

        try {
            let updatedJob;
            try {
                updatedJob = await onNoteAdded({
                    content: html || plainText,
                    noteType,
                    customerVisible,
                    workItemUuid,
                });
                setStepStatus(0, 'done');
            } catch (err) {
                setStepStatus(0, 'error');
                setProgressError(err?.response?.data?.message || err?.message || 'Failed to save note');
                setTimeout(() => setProgressSteps(null), 2500);
                return;
            }

            if (hasImages && updatedJob) {
                const newNote = [...(updatedJob.notes || [])].sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                )[0];

                if (newNote) {
                    for (let i = 0; i < pendingFiles.length; i++) {
                        const { file } = pendingFiles[i];
                        setStepStatus(i + 1, 'active');
                        try {
                            await uploadNoteImageApi(jobUuid, newNote.uuid, file);
                            setStepStatus(i + 1, 'done');
                        } catch (err) {
                            // Surface the actual failure instead of swallowing it — without
                            // this the user sees the note save succeed and the image silently
                            // not attach (the original bug class on this flow).
                            setStepStatus(i + 1, 'error');
                            setProgressError(
                                err?.response?.data?.message ||
                                err?.message ||
                                `Failed to upload ${file.name}`
                            );
                        }
                    }
                    if (onRefreshJob) {
                        try { await onRefreshJob(); } catch { /* non-fatal */ }
                    }
                } else {
                    // Caller returned a job that has no notes[] — this means onNoteAdded
                    // returned the Redux action envelope instead of the unwrapped payload.
                    // Fail loudly rather than dropping the image upload silently.
                    setProgressError(
                        'Note saved but the new note could not be located in the response. ' +
                        'Images were not uploaded. (If you are seeing this consistently, check ' +
                        'the page\u2019s onAddNote handler — it should return the awaited thunk payload, ' +
                        'not the dispatch envelope.)'
                    );
                }
            } else if (hasImages && !updatedJob) {
                setProgressError('Note saved but the server response was empty. Images were not uploaded.');
            }

            await new Promise(r => setTimeout(r, 600));
            setProgressSteps(null);

            // Clear editor but keep the selected work item — common workflow is
            // multiple notes on the same task in sequence
            if (editorRef.current) editorRef.current.innerHTML = '';
            setIsEmpty(true);
            setActiveFormats({});
            pendingFiles.forEach(f => URL.revokeObjectURL(f.previewUrl));
            setPendingFiles([]);
            // NOTE: customerVisible is intentionally NOT reset — the tech's
            // choice persists across consecutive notes. Flipping it per-note
            // would be tedious when adding a batch of customer-facing updates.

        } catch (err) {
            setProgressError(err?.message || 'Unexpected error');
            setTimeout(() => setProgressSteps(null), 3000);
        }
    };

    const noWorkItems = workItems.length === 0;
    const canSubmit   = !isEmpty || pendingFiles.length > 0;

    // ── Render ──────────────────────────────────────────────────────────────

    return (
        <>
            {progressSteps && <ProgressDialog steps={progressSteps} error={progressError} />}

            <div className={`border rounded-xl overflow-hidden bg-white shadow-sm transition-colors ${
                workItemError ? 'border-red-300' : 'border-gray-200'
            }`}>

                {/* Work item selector — prominent, sits above the editor */}
                <div className={`px-3.5 pt-3 pb-2.5 border-b ${
                    workItemError ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50'
                }`}>
                    <div className="flex items-center gap-2">
                        <Wrench size={13} className={`shrink-0 ${workItemError ? 'text-red-400' : 'text-violet-500'}`} />
                        <label className={`text-xs font-medium shrink-0 ${workItemError ? 'text-red-600' : 'text-gray-600'}`}>
                            Work item <span className="text-red-500">*</span>
                        </label>
                        {noWorkItems ? (
                            <span className="text-xs text-gray-400 italic">No work items on this job yet</span>
                        ) : (
                            <select
                                value={workItemUuid}
                                onChange={e => { setWorkItemUuid(e.target.value); setWorkItemError(false); }}
                                className={`flex-1 text-xs border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-violet-400/30 transition-colors ${
                                    workItemUuid
                                        ? 'bg-white border-violet-300 text-violet-800 font-medium'
                                        : workItemError
                                            ? 'bg-white border-red-300 text-gray-500'
                                            : 'bg-white border-gray-200 text-gray-500'
                                }`}
                            >
                                <option value="">Select a work item…</option>
                                {workItems.map(wi => (
                                    <option key={wi.uuid} value={wi.uuid}>
                                        {truncate(wi.description, 60)}
                                        {/* Labelled, because attaching a note to work the
                                            customer refused is a deliberate act — recording
                                            what was advised and shown — not a mis-click. */}
                                        {wi.status === 'DECLINED' ? ' — declined' : ''}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                    {workItemError && (
                        <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                            <AlertCircle size={11} className="shrink-0" />
                            Please select a work item before adding a note
                        </p>
                    )}
                </div>

                {/* Measurement panel for the selected work item.
                    Rendered via the parent-supplied render prop so this file stays
                    free of any direct dependency on JobDetailModal. Only mounts
                    when (a) a work item is actually selected and (b) the parent
                    actually provided the render prop. */}
                {workItemUuid && renderMeasurementsPanel && (() => {
                    const selectedItem = workItems.find(wi => wi.uuid === workItemUuid);
                    if (!selectedItem) return null;
                    return (
                        <div className="px-3.5 py-3 border-b border-gray-100 bg-gray-50/40">
                            {renderMeasurementsPanel(selectedItem)}
                        </div>
                    );
                })()}

                {/* Toolbar.

                    On a phone the text-formatting half is hidden: bold, italic, lists and
                    inline code are not what a mechanic standing next to a car needs, and
                    the row costs a third of the visible form. The image and camera
                    buttons stay — the photo IS the note most of the time, and they are
                    the only route to one. */}
                <div className="flex items-center gap-0.5 px-3 py-2 border-b border-gray-100 bg-white">
                    {!compact && <>
                    <ToolbarBtn title="Bold (Ctrl+B)" active={activeFormats.bold}
                                onMouseDown={e => { e.preventDefault(); execCmd('bold'); }}>
                        <Bold size={13} />
                    </ToolbarBtn>
                    <ToolbarBtn title="Italic (Ctrl+I)" active={activeFormats.italic}
                                onMouseDown={e => { e.preventDefault(); execCmd('italic'); }}>
                        <Italic size={13} />
                    </ToolbarBtn>
                    <ToolbarBtn title="Underline (Ctrl+U)" active={activeFormats.underline}
                                onMouseDown={e => { e.preventDefault(); execCmd('underline'); }}>
                        <Underline size={13} />
                    </ToolbarBtn>
                    <div className="w-px h-4 bg-gray-200 mx-1" />
                    <ToolbarBtn title="Bullet list" active={activeFormats.ul}
                                onMouseDown={e => { e.preventDefault(); execCmd('insertUnorderedList'); }}>
                        <List size={13} />
                    </ToolbarBtn>
                    <ToolbarBtn title="Numbered list" active={activeFormats.ol}
                                onMouseDown={e => { e.preventDefault(); execCmd('insertOrderedList'); }}>
                        <ListOrdered size={13} />
                    </ToolbarBtn>
                    <div className="w-px h-4 bg-gray-200 mx-1" />
                    <ToolbarBtn title="Inline code"
                                onMouseDown={e => {
                                    e.preventDefault();
                                    const el = editorRef.current;
                                    if (!el) return;
                                    el.focus();
                                    const sel = window.getSelection();
                                    if (sel?.rangeCount && !sel.isCollapsed) {
                                        const range = sel.getRangeAt(0);
                                        const code  = document.createElement('code');
                                        code.style.cssText = 'background:#f3f4f6;padding:0 4px;border-radius:3px;font-size:0.75em;font-family:monospace';
                                        range.surroundContents(code);
                                        sel.removeAllRanges();
                                    } else {
                                        document.execCommand('insertHTML', false,
                                            '<code style="background:#f3f4f6;padding:0 4px;border-radius:3px;font-size:0.75em;font-family:monospace">code</code>');
                                    }
                                    handleEditorInput();
                                }}>
                        <Code size={13} />
                    </ToolbarBtn>
                    </>}
                    <div className="flex-1" />
                    <ToolbarBtn title="Attach from gallery"
                                onMouseDown={e => { e.preventDefault(); galleryRef.current?.click(); }}>
                        <ImageIcon size={14} />
                    </ToolbarBtn>
                    <ToolbarBtn title="Take a photo"
                                onMouseDown={e => { e.preventDefault(); cameraRef.current?.click(); }}>
                        <Camera size={14} />
                    </ToolbarBtn>
                </div>

                {/* Hidden file inputs */}
                <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden"
                       onChange={e => { handleFiles(Array.from(e.target.files)); e.target.value = ''; }} />
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                       onChange={e => { handleFiles(Array.from(e.target.files)); e.target.value = ''; }} />

                {/* Editor */}
                <style>{`
                    .wadakaroo-editor:empty:before {
                        content: attr(data-placeholder);
                        color: #9ca3af;
                        pointer-events: none;
                    }
                    .wadakaroo-editor ul { list-style-type: disc; padding-left: 1.25rem; margin: 0.25rem 0; }
                    .wadakaroo-editor ol { list-style-type: decimal; padding-left: 1.25rem; margin: 0.25rem 0; }
                    .wadakaroo-editor li { margin: 0.1rem 0; }
                    .wadakaroo-editor b, .wadakaroo-editor strong { font-weight: 700; }
                    .wadakaroo-editor i, .wadakaroo-editor em { font-style: italic; }
                    .wadakaroo-editor u { text-decoration: underline; }
                `}</style>
                <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder="Add a note…"
                    className="wadakaroo-editor min-h-[96px] max-h-48 overflow-y-auto px-3.5 py-3 text-sm text-gray-800 focus:outline-none leading-relaxed"
                    onInput={handleEditorInput}
                    onKeyDown={handleKeyDown}
                    onClick={updateActiveFormats}
                />

                {/* Pending image previews */}
                {pendingFiles.length > 0 && (
                    <div className="px-3.5 pb-2 flex flex-wrap gap-2 border-t border-gray-100 pt-2">
                        {pendingFiles.map(({ id, previewUrl, file }) => (
                            <div key={id} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200 group">
                                <img src={previewUrl} alt={file.name} className="w-full h-full object-cover" />
                                <button type="button" onClick={() => removeFile(id)}
                                        className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <X size={11} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Inline error */}
                {submitError && (
                    <div className="mx-3.5 mb-2 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        <AlertCircle size={13} className="shrink-0" />{submitError}
                    </div>
                )}

                {/* Footer */}
                <div className="flex items-center gap-2 px-3 py-2.5 border-t border-gray-100 bg-gray-50 flex-wrap">
                    {/* Note type is temporarily locked to Progress Update.
                        The other types (General / Inspection / Customer Comms / Parts Order /
                        Handover) are not yet wired into the customer-facing report templates
                        or the dashboard filters, so exposing them now would silently mis-label
                        notes. Re-enable by restoring the <select> below once those are ready.

                        Hidden entirely on a phone: it is a disabled label showing the only
                        value it can have, so it costs a row and tells the mechanic nothing. */}
                    {!compact && (
                    <div
                        className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-gray-100 text-gray-500 select-none cursor-not-allowed"
                        title="Note type is locked to Progress Update for now"
                    >
                        Progress Update
                    </div>
                    )}

                    {/* ⚠️ Also hidden on a phone, which means every note added there is
                        CUSTOMER-VISIBLE — the default this toggle starts in. A tech who
                        needs an internal-only note has to use the desktop or tablet
                        view. Removing the control does not change the default; it
                        removes the ability to depart from it. */}
                    {!compact && (
                    <button
                        type="button"
                        onClick={() => setCustomerVisible(v => !v)}
                        title={customerVisible
                            ? 'This note will appear in the customer\u2019s service report. Click to make internal only.'
                            : 'This note is internal only \u2014 customer will not see it. Click to make customer-visible.'}
                        className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border font-medium transition-all
                        ${customerVisible
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100 ring-1 ring-emerald-100'
                            : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200'}`}
                    >
                        {customerVisible
                            ? <><Eye size={12} /> Customer will see this</>
                            : <><Lock size={12} /> Internal only</>}
                    </button>
                    )}

                    <div className="flex-1" />

                    <button type="button" onClick={handleSubmit}
                            disabled={!canSubmit || noWorkItems}
                            className="flex items-center gap-1.5 px-4 py-1.5 bg-violet-600 text-white text-xs font-medium rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        <Plus size={12} /> Add Note
                    </button>
                </div>
            </div>
        </>
    );
}

// ── Note card ─────────────────────────────────────────────────────────────────

function NoteCard({ note }) {
    const [lightboxIdx, setLightboxIdx] = useState(null);
    const images = note.images || [];

    return (
        <>
            <div className="flex gap-3 bg-white rounded-xl border border-gray-100 p-3.5">
                <div className="w-7 h-7 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {initials(note.authorName)}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className="text-xs font-semibold text-gray-800">{note.authorName}</span>
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${NOTE_TYPE_COLORS[note.noteType] || 'bg-gray-100 text-gray-600'}`}>
                            {noteTypeLabel(note.noteType)}
                        </span>
                        {note.customerVisible ? (
                            <span
                                className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-[10px] flex items-center gap-0.5 font-medium"
                                title="This note appears in the customer's service report"
                            >
                                <Eye size={9} /> Customer
                            </span>
                        ) : (
                            <span
                                className="px-1.5 py-0.5 bg-gray-100 text-gray-600 border border-gray-200 rounded-full text-[10px] flex items-center gap-0.5 font-medium"
                                title="Internal only \u2014 customer will not see this note"
                            >
                                <Lock size={9} /> Internal
                            </span>
                        )}
                        <span className="text-[10px] text-gray-400 ml-auto whitespace-nowrap">{formatDate(note.createdAt)}</span>
                    </div>

                    {note.content && (note.content.startsWith('<') ? (
                        <div className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none
                            [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono
                            [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4"
                             dangerouslySetInnerHTML={safeNoteHtml(note.content)} />
                    ) : (
                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{decodeBasicEntities(note.content)}</p>
                    ))}

                    {images.length > 0 && (
                        <NoteThumbnails images={images} onOpen={setLightboxIdx} />
                    )}
                </div>
            </div>

            {lightboxIdx !== null && (
                <Lightbox images={images} startIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />
            )}
        </>
    );
}

// ── Work Item Note Group ──────────────────────────────────────────────────────

/**
 * Collapsible section for one work item's notes.
 * Header shows work item description, status badge, and note count.
 * Starts expanded. Notes are sorted newest-first within the group.
 */
function WorkItemNoteGroup({ workItem, notes }) {
    const [open, setOpen] = useState(true);

    const statusLabel = workItem?.status ? workItem.status.replace(/_/g, ' ') : null;
    const statusClass = WORK_ITEM_STATUS_BADGE[workItem?.status] || 'bg-gray-100 text-gray-500';

    // Number of notes in this group that will appear in the customer report
    const customerCount = notes.filter(n => n.customerVisible).length;

    return (
        <div className="rounded-xl border border-gray-200 overflow-hidden">

            {/* Group header */}
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
                <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                    <Wrench size={13} className="text-violet-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate" title={workItem?.description}>
                        {workItem?.description || 'Work item'}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                        {statusLabel && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${statusClass}`}>
                                {statusLabel}
                            </span>
                        )}
                        <span className="text-[10px] text-gray-400">
                            {notes.length} {notes.length === 1 ? 'note' : 'notes'}
                        </span>
                        {customerCount > 0 && (
                            <span
                                className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-0.5 font-medium"
                                title={`${customerCount} of ${notes.length} will appear in the customer report`}
                            >
                                <Eye size={9} /> {customerCount}
                            </span>
                        )}
                    </div>
                </div>
                {open
                    ? <ChevronUp size={15} className="text-gray-400 shrink-0" />
                    : <ChevronDown size={15} className="text-gray-400 shrink-0" />
                }
            </button>

            {/* Notes list */}
            {open && (
                <div className="px-3 py-3 space-y-2 bg-white border-t border-gray-100">
                    {notes.map(note => (
                        <NoteCard key={note.uuid} note={note} />
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function JobNotesTab({
                                        job, onNoteAdded, onAddNote, onRefreshJob, operationLoading,
                                        // Phone: drops the text-formatting buttons and the
                                        // footer chips. Photos and the work-item picker stay.
                                        compact = false,
                                        // Render-prop for the per-work-item measurement panel. Optional — when not
                                        // provided, the work-item dropdown still works for note linking, just without
                                        // the measurement UI. JobDetailModal supplies this; other callers (e.g. the
                                        // tablet view) may opt out. Passed as a render function rather than a
                                        // component reference to keep JobNotesTab free of any direct import from
                                        // JobDetailModal, which itself imports JobNotesTab (circular import).
                                        renderMeasurementsPanel,
                                    }) {

    // Support both prop names — JobsPage passes onAddNote, other callers may use onNoteAdded
    const handleNoteAdded = onNoteAdded || onAddNote;

    // EVERY work item can carry notes and photos, declined ones included.
    //
    // Declined items used to be filtered out, which removed the evidence exactly where
    // it matters most: "we advised the timing belt, showed the customer the wear, they
    // declined" is the note a workshop needs months later when the belt fails and the
    // conversation turns to who knew what. With the item unselectable, that note and
    // its photo had nowhere to go — the reasoning was lost, or worse, attached to an
    // unrelated item.
    const linkableWorkItems = job?.workItems || [];

    const notes = job?.notes || [];

    // Group notes by workItemUuid, newest-first within each group
    const grouped = {};
    const unlinked = [];

    for (const note of notes) {
        if (note.workItemUuid) {
            if (!grouped[note.workItemUuid]) grouped[note.workItemUuid] = [];
            grouped[note.workItemUuid].push(note);
        } else {
            unlinked.push(note);
        }
    }
    for (const uuid of Object.keys(grouped)) {
        grouped[uuid].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
    unlinked.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    // Sort groups by most recently active (the group with the newest note comes first)
    const groupedEntries = Object.entries(grouped).sort(([, aNotes], [, bNotes]) =>
        new Date(bNotes[0]?.createdAt || 0) - new Date(aNotes[0]?.createdAt || 0)
    );

    // Resolve the work item object for a group's header.
    // Falls back to the note's denormalised workItemDescription so headers still render
    // correctly after the job is pruned by the nightly DataCleanupJob.
    const resolveWorkItem = (workItemUuid, groupNotes) => {
        const fromJob = (job?.workItems || []).find(wi => wi.uuid === workItemUuid);
        if (fromJob) return fromJob;
        const note = groupNotes[0];
        return {
            uuid: workItemUuid,
            description: note?.workItemDescription || 'Work item',
            status: null,
        };
    };

    return (
        <div className="flex flex-col gap-4">
            <AddNoteForm
                jobUuid={job?.uuid}
                workItems={linkableWorkItems}
                compact={compact}
                onNoteAdded={handleNoteAdded}
                onRefreshJob={onRefreshJob}
                renderMeasurementsPanel={renderMeasurementsPanel}
            />

            {notes.length === 0 ? (
                <div className="text-center py-10 text-gray-400 text-sm bg-gray-50 rounded-xl">
                    <MessageSquare size={22} className="mx-auto mb-2 opacity-30" />
                    No notes yet — add the first one above
                </div>
            ) : (
                <div className="space-y-3">
                    {/* Work-item-linked groups, most recently active first */}
                    {groupedEntries.map(([workItemUuid, groupNotes]) => (
                        <WorkItemNoteGroup
                            key={workItemUuid}
                            workItem={resolveWorkItem(workItemUuid, groupNotes)}
                            notes={groupNotes}
                        />
                    ))}

                    {/* Legacy / unlinked notes — dashed border to visually distinguish */}
                    {unlinked.length > 0 && (
                        <div className="rounded-xl border border-dashed border-gray-200 overflow-hidden">
                            <div className="px-4 py-2.5 bg-gray-50 flex items-center gap-2 border-b border-dashed border-gray-200">
                                <MessageSquare size={13} className="text-gray-400" />
                                <span className="text-xs font-medium text-gray-500">
                                    General notes ({unlinked.length})
                                </span>
                            </div>
                            <div className="px-3 py-3 space-y-2 bg-white">
                                {unlinked.map(note => (
                                    <NoteCard key={note.uuid} note={note} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}